import type { JSX } from 'react';
import { Node, mergeAttributes, ReactNodeViewRenderer, NodeViewWrapper, type NodeViewProps } from '@tiptap/react';
import { useRef, useState } from 'react';
import {
  AlignLeftOutlined,
  AlignCenterOutlined,
  AlignRightOutlined,
  ColumnWidthOutlined,
  ExportOutlined,
  DeleteOutlined,
} from '@ant-design/icons';

/**
 * ResizableImage — картинка с маркерами ресайза + плавающей панелью действий
 * (выравнивание / открыть / удалить). Заменяет @tiptap/extension-image.
 *
 * Хранение в markdown: vault — это .md (`![alt](url)`), который НЕ хранит
 * ширину/выравнивание. Поэтому кодируем их в query URL: `url?w=420&a=center`.
 * Сервер статики (vault-asset / attachments) query игнорирует → картинка
 * грузится; а мы парсим w/a обратно. Полный round-trip через обычный `![](…)`.
 *
 * align: left | center | right | full (на всю ширину). width — px (null = авто).
 */

type Align = 'left' | 'center' | 'right' | 'full';

function buildSrc(src: string, width: number | null, align: Align | null): string {
  const base = src.split('?')[0];
  const qs: string[] = [];
  if (width && width > 0) qs.push(`w=${Math.round(width)}`);
  if (align && align !== 'left') qs.push(`a=${align}`);
  return qs.length ? `${base}?${qs.join('&')}` : base;
}

function parseSrc(raw: string): { src: string; width: number | null; align: Align } {
  const [base, query] = raw.split('?');
  const params = new URLSearchParams(query || '');
  const w = parseInt(params.get('w') || '', 10);
  const a = (params.get('a') as Align) || 'left';
  return {
    src: base,
    width: Number.isFinite(w) && w > 0 ? w : null,
    align: (['left', 'center', 'right', 'full'] as string[]).includes(a) ? a : 'left',
  };
}

export const ResizableImage = Node.create({
  name: 'image',
  group: 'block',
  atom: true,
  draggable: true,
  selectable: true,

  addAttributes() {
    return {
      src: { default: null },
      alt: { default: '' },
      width: { default: null },
      align: { default: 'left' as Align },
      // Подпись под картинкой (фаза 0.3) — в markdown это title: ![alt](url "подпись").
      caption: { default: '' },
    };
  },

  parseHTML() {
    return [
      {
        // Картинка с подписью: <figure class="fk-figure"><img><figcaption>
        tag: 'figure.fk-figure',
        getAttrs: (el: HTMLElement) => {
          const img = el.querySelector('img');
          if (!img) return false;
          const { src, width, align } = parseSrc(img.getAttribute('src') || '');
          const caption = el.querySelector('figcaption')?.textContent?.trim() || '';
          return { src, alt: img.getAttribute('alt') || '', width, align, caption };
        },
      },
      {
        tag: 'img[src]',
        getAttrs: (el: HTMLElement) => {
          const raw = el.getAttribute('src') || '';
          const { src, width, align } = parseSrc(raw);
          return { src, alt: el.getAttribute('alt') || '', width, align };
        },
      },
    ];
  },

  renderHTML({ HTMLAttributes }: { HTMLAttributes: Record<string, unknown> }) {
    const src = String(HTMLAttributes.src ?? '');
    const width = (HTMLAttributes.width as number | null) ?? null;
    const align = (HTMLAttributes.align as Align | null) ?? 'left';
    // В markdown идёт src с зашитыми w/a (round-trip), display-стиль — на читалке.
    const img: [string, Record<string, unknown>] = ['img', mergeAttributes({ src: buildSrc(src, width, align), alt: HTMLAttributes.alt, class: 'wiki-embed' })];
    const caption = String(HTMLAttributes.caption ?? '').trim();
    if (!caption) return img;
    return ['figure', { class: 'fk-figure' }, img, ['figcaption', {}, caption]];
  },

  addNodeView() {
    return ReactNodeViewRenderer(ResizableImageView);
  },
});

function ResizableImageView(props: NodeViewProps): JSX.Element {
  const { node, updateAttributes, deleteNode, selected } = props;
  const src: string = node.attrs.src ?? '';
  const alt: string = node.attrs.alt ?? '';
  const width: number | null = node.attrs.width ?? null;
  const align: Align = (node.attrs.align as Align) ?? 'left';
  const caption: string = node.attrs.caption ?? '';
  const wrapRef = useRef<HTMLDivElement>(null);
  const [dragging, setDragging] = useState(false);

  // Ресайз перетаскиванием правого/угловых маркеров.
  const startResize = (e: React.PointerEvent, dir: 1 | -1): void => {
    e.preventDefault();
    e.stopPropagation();
    const img = wrapRef.current?.querySelector('img');
    const startX = e.clientX;
    const startW = img?.getBoundingClientRect().width ?? 300;
    const maxW = wrapRef.current?.parentElement?.getBoundingClientRect().width ?? 900;
    setDragging(true);
    const onMove = (ev: PointerEvent): void => {
      const next = Math.max(80, Math.min(maxW, startW + (ev.clientX - startX) * dir));
      updateAttributes({ width: Math.round(next), align: align === 'full' ? 'left' : align });
    };
    const onUp = (): void => {
      setDragging(false);
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  const justify = align === 'center' ? 'center' : align === 'right' ? 'flex-end' : 'flex-start';
  const imgWidth = align === 'full' ? '100%' : width ? `${width}px` : 'auto';
  const maxW = align === 'full' ? '100%' : '100%';

  const handleStyle: React.CSSProperties = {
    position: 'absolute', width: 12, height: 44, borderRadius: 6,
    background: 'var(--fk-orange)', border: '2px solid #fff', boxShadow: '0 1px 4px rgba(0,0,0,.25)',
    cursor: 'ew-resize', top: '50%', transform: 'translateY(-50%)', zIndex: 3,
  };

  return (
    <NodeViewWrapper className="fk-rimg" style={{ display: 'flex', justifyContent: justify, margin: '14px 0' }}>
      <div
        ref={wrapRef}
        contentEditable={false}
        style={{ position: 'relative', display: 'inline-block', maxWidth: '100%', lineHeight: 0,
          width: align === 'full' ? '100%' : 'auto',
          outline: selected ? '2px solid var(--fk-orange)' : '2px solid transparent', outlineOffset: 2, borderRadius: 8 }}
      >
        <img src={buildSrc(src, null, null)} alt={alt} draggable={false}
          style={{ width: imgWidth, maxWidth: maxW, height: 'auto', display: 'block', borderRadius: 8 }} />
        {/* Подпись (фаза 0.3): показываем поле при выборе или если подпись уже есть */}
        {(selected || caption) && (
          <input
            value={caption}
            onChange={(e) => updateAttributes({ caption: e.target.value })}
            placeholder="Подпись под картинкой"
            className="fk-rimg-caption"
            style={{ width: '100%', boxSizing: 'border-box', marginTop: 6, border: '1px solid var(--fk-border)', borderRadius: 8, padding: '5px 9px', font: 'inherit', fontSize: 12.5, color: 'var(--fk-text-muted)', textAlign: 'center', background: 'var(--fk-surface)', outline: 'none', lineHeight: 1.4 }}
          />
        )}

        {/* Маркеры ресайза (по бокам) — видны при выборе/наведении. */}
        {selected && align !== 'full' && (
          <>
            <span style={{ ...handleStyle, right: -6 }} onPointerDown={(e) => startResize(e, 1)} />
            <span style={{ ...handleStyle, left: -6, cursor: 'ew-resize' }} onPointerDown={(e) => startResize(e, -1)} />
          </>
        )}

        {/* Плавающая панель действий (при выборе, и не во время ресайза). */}
        {selected && !dragging && (
          <div
            contentEditable={false}
            style={{ position: 'absolute', top: -46, left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: 2,
              background: '#fff', border: '1px solid var(--fk-border)', borderRadius: 10, padding: 4,
              boxShadow: '0 8px 28px rgba(37,29,74,.16)', zIndex: 4, whiteSpace: 'nowrap' }}
          >
            <RBtn title="По левому краю" active={align === 'left'} onClick={() => updateAttributes({ align: 'left' })}><AlignLeftOutlined /></RBtn>
            <RBtn title="По центру" active={align === 'center'} onClick={() => updateAttributes({ align: 'center' })}><AlignCenterOutlined /></RBtn>
            <RBtn title="По правому краю" active={align === 'right'} onClick={() => updateAttributes({ align: 'right' })}><AlignRightOutlined /></RBtn>
            <RBtn title="На всю ширину" active={align === 'full'} onClick={() => updateAttributes({ align: 'full', width: null })}><ColumnWidthOutlined /></RBtn>
            <span style={{ width: 1, background: 'var(--fk-border)', margin: '2px 3px' }} />
            <RBtn title="Открыть оригинал" onClick={() => window.open(buildSrc(src, null, null), '_blank', 'noopener')}><ExportOutlined /></RBtn>
            <RBtn title="Удалить" danger onClick={() => deleteNode()}><DeleteOutlined /></RBtn>
          </div>
        )}
      </div>
      <style>{`
        .fk-rimg .fk-rimg-btn{width:28px;height:28px;display:grid;place-items:center;border:none;background:transparent;color:var(--fk-text-2);border-radius:7px;cursor:pointer;font-size:14px}
        .fk-rimg .fk-rimg-btn:hover{background:var(--fk-bg-subtle)}
        .fk-rimg .fk-rimg-btn[data-on="true"]{background:var(--fk-orange-soft);color:var(--fk-orange)}
        .fk-rimg .fk-rimg-btn[data-danger="true"]:hover{background:rgba(229,57,53,.1);color:var(--fk-error)}
      `}</style>
    </NodeViewWrapper>
  );
}

function RBtn({ title, active, danger, onClick, children }: { title: string; active?: boolean; danger?: boolean; onClick: () => void; children: React.ReactNode }): JSX.Element {
  return (
    <button type="button" className="fk-rimg-btn" title={title} data-on={active ? 'true' : undefined} data-danger={danger ? 'true' : undefined}
      onMouseDown={(e) => { e.preventDefault(); onClick(); }}>
      {children}
    </button>
  );
}
